
// src/pages/Passages.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import DeletePatientButton from '../components/DeletePatientButton';

import PageLayout from '../components/PageLayout';
import RoomBadge from '../components/RoomBadge';
import StepIndicator from '../components/StepIndicator';
import EmptyState from '../components/EmptyState';
import TpBadge from '../components/TpBadge';

import { ReactComponent as UrgentIcon } from '../assets/urgent.svg';
import { showTime, fromNow, showTimeWDate } from '../utils/dateHelpers';
import { getDisplayMusiqId } from '../utils/musiqId';

import { socket } from '../socket';
import { getStep, STATUS_MAP } from '../constants/statusDefinitions';
import { CONFIG } from '../config/config';

import { listGroups, listSalles, listTypesSalles } from '../api/salles';
import { useUser } from '../context/UserContext';
import { format } from 'date-fns';

const API_URL = (CONFIG.API_URL || '/api').replace(/\/+$/, '');

// ---- Debug util (active si REACT_APP_DEBUG=1 ou window.__MUSIQ_DEBUG_SALLES__ = true)
const __DEBUG_ON__ = process.env.REACT_APP_DEBUG === '1';
const dbg = (...args) => {
  try {
    const win = typeof window !== 'undefined' ? window : null;
    const enabled = __DEBUG_ON__ || (win && win.__MUSIQ_DEBUG_SALLES__ === true);
    if (enabled) console.log('[SALLES]', ...args);
  } catch {}
};

const toneForStep = (s) => {
  const n = Number(s);
  if ([1, 2, 5, 6].includes(n))
    return { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' };
  if ([3, 4].includes(n)) return { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' };
  return { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' };
};

const entryOf = (p) => p.heureEntree || p.heure_entree || p.createdAt || p.created_at || null;
const getUid = (p) => getDisplayMusiqId(p);

/* =============================================================== */

export default function Passages({ patients = [], setPatients }) {
  const [group, setGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [patientsByGroup, setPatientsByGroup] = useState({})
  const [salles, setSalles] = useState([]);
  const [typesSalles, setTypesSalles] = useState([]);

  useEffect(() => {
    listTypesSalles().then(setTypesSalles).catch(() => {});
  }, []);

  const getTypeSalleLibelle = (typeId) => {
    if (!typeId) return null;
    const found = typesSalles.find((ts) => ts.id === typeId);
    return found ? found.libelle : null;
  };
  const { etablissement, chosenGroup, handleSetChosenGroup: setChosenGroup, user: me } = useUser();
  const navigate = useNavigate();
  const [sheetStatus, setSheetStatus] = useState({});
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  useEffect(() => {
    setPatientsByGroup(getPatientsByGroup(patientsActifs, groups));
  }, [groups, patients]);

  const fetchGroups = async () => {
    try {
      const fGroups = await listGroups();
      const myGroups = fGroups.filter(
        (grp) => grp.etablissement.etablissement_id === etablissement.etablissement_id,
      );
      setGroups(myGroups);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSalles = async () => {
    try {
      const fSalles = await listSalles();
      setSalles(fSalles);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [etablissement]);


  const ALL_GROUP = { group_id: 'all-patients', group_libelle: 'Tous', salles: [] };

  useEffect(() => {
    if (groups.length) {
      if (chosenGroup && (groups.some((group) => group.group_id === chosenGroup.group_id) || chosenGroup?.group_id === 'all-patients')) {
        setGroup(chosenGroup);
      } else {
        setGroup(groups[0]);
        setChosenGroup(groups[0]);
      }
    } else {
      // Pas de groupe configuré → afficher "Tous" par défaut
      setGroup(ALL_GROUP);
      setChosenGroup(ALL_GROUP);
    }
  }, [groups, chosenGroup]);

  useEffect(() => {
    fetchSalles();
  }, []);

  function getPatientsByGroup(patients, groups) {
    const res = groups.reduce((acc, group) => {
      const salleIds = new Set(
        group.salles.map((salle) => salle.salle_id).filter((id) => id != null),
      );
      acc[group.group_id] = patients.filter((patient) => salleIds.has(patient.salle_id));
      return acc;
    }, {});
    res['all-patients'] = allPatients;

    return res;
  }

  function getSalleNomFromId(id, salles){
    return id == null ? '-' : (salles.find((s) => s.salle_id === id)?.salle_nom ?? '-');
  }

  const isAdmin = me?.role === 'admin' || (Array.isArray(me?.roles) && me.roles.includes('admin'));
  const isMedecin =
    me?.role === 'medecin' || (Array.isArray(me?.roles) && me.roles.includes('medecin'));
  const canDelete = isAdmin || isMedecin;

  /* -------- sockets + fallback fetch -------- */
  const handlePatientsList = useCallback(
    (list) => {
      dbg(
        'WS patientsList ->',
        list.map((p) => ({
          id: p.id,
          nom: p.nom,
          prenom: p.prenom,
          step: Number(getStep(p)),
         // salle: p.salle,
          salle_id: p.salle_id,
          statut: p.statut,
        })),
      );
      setPatients(list);
    },
    [setPatients],
  );

  const handlePatientDeleted = ({ id }) => {
    setPatients((prev) => prev.filter((p) => p.id !== id));
  };

  const handlePatientUpdated = useCallback(
    (raw) => {
      const incoming = raw;
      if (!incoming?.id) return;
      setPatients((prev) => {
        const before = prev.find((x) => x.id === incoming.id);
        if (!before) return [...prev, incoming];
        const merged = { ...before, ...incoming }; // 🔒 merge non destructif
        return prev.map((x) => (x.id === merged.id ? merged : x));
      });
    },
    [setPatients],
  );

  const ask = useCallback(() => {
    dbg('socket.emit("getPatients")');
    socket.emit('getPatients');
  }, []);

  useEffect(() => {
    socket.on('patientsList', handlePatientsList);
    socket.on('patients_list', handlePatientsList);
    socket.on('patientUpdated', handlePatientUpdated);
    socket.on('patientCreated', handlePatientUpdated);
    socket.on('patientDeleted', ({ id }) => {
      setPatients((prev) => prev.filter((p) => p.id !== id));
    });
    socket.on('patientsCleared', () => setPatients([]));
    // 👇 Abonnement status-driven du calcul Sheet
    const onSheetStatus = ({ passageId, status }) => {
      if (!passageId || !status) return;
      dbg('WS sheet:status', { passageId, status });
      setSheetStatus((m) => ({ ...m, [passageId]: status }));
    };
    const onSheetProgress = (payload) => {
      // compat: translate legacy started/done → pending/ready
      const s =
        payload?.stage === 'started' ? 'pending' : payload?.stage === 'done' ? 'ready' : null;
      if (payload?.passageId && s) {
        dbg('WS sheet:progress', { passageId: payload.passageId, stage: payload.stage, mapped: s });
        setSheetStatus((m) => ({ ...m, [payload.passageId]: s }));
      }
    };
    socket.on('sheet:status', onSheetStatus);
    socket.on('sheet:progress', onSheetProgress);
    socket.on('connect', ask);
    if (socket.connected) ask();

    return () => {
      socket.off('patientsList', handlePatientsList);
      socket.off('patients_list', handlePatientsList);
      socket.off('patientUpdated', handlePatientUpdated);
      socket.off('patientCreated', handlePatientUpdated);
      socket.off('patientDeleted', handlePatientDeleted);
      socket.off('connect', ask);
      socket.off('sheet:status', onSheetStatus);
      socket.off('sheet:progress', onSheetProgress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlePatientsList, handlePatientUpdated, ask]);


  const arrivalOf = (p) => p.heureEntree || p.heure_entree || p.createdAt || p.created_at || null;
  /* -------- dérivés -------- */
  const patientsActifs = useMemo(
    () =>
    {
      const base =  (patients || []).filter((p) => {
        const s = Number(getStep(p));
        return Number.isFinite(s) && s >= 1 && s <= 6;
      });
      return base.slice().sort((a, b) => {
        if (a.urgence !== b.urgence) return a.urgence ? -1 : 1;
        const ta = new Date(arrivalOf(a) || 0).getTime();
        const tb = new Date(arrivalOf(b) || 0).getTime();
        return ta - tb;
      });
    },
    [patients],
  );

  const allPatients = useMemo(
    () =>
    {
      const base =  (patients || []).filter((p) => {
        const s = Number(getStep(p));
        return (
          Number.isFinite(s) &&
          s >= 0 &&
          s <= 6 &&
          p.etablissement_id === etablissement.etablissement_id
        );
      });
      return base.slice().sort((a, b) => {
        if (a.urgence !== b.urgence) return a.urgence ? -1 : 1;
        const ta = new Date(arrivalOf(a) || 0).getTime();
        const tb = new Date(arrivalOf(b) || 0).getTime();
        return ta - tb;
      });
    },
    [patients, etablissement],
  );

  // 🔎 Log des diffs sur la liste complète (step/salle)
  const prevSnapRef = useRef([]);
  useEffect(() => {
    const snap = (patients || []).map((p) => ({
      id: p.id,
      step: Number(getStep(p)),
      salle_id: p.salle_id ?? null,
      statut: p.statut ?? null,
    }));
    const prev = prevSnapRef.current;
    if (prev.length === 0) {
      dbg('patients initial snapshot', snap);
    } else {
      const prevMap = new Map(prev.map((x) => [x.id, x]));
      const changes = [];
      snap.forEach((row) => {
        const before = prevMap.get(row.id);
        if (!before) {
          changes.push({ id: row.id, type: 'new', step: row.step, salle: row.salle });
          return;
        }
        const c = {};
        if (before.step !== row.step) c.step = [before.step, row.step];
        if (String(before.salle || '') !== String(row.salle || ''))
          c.salle = [before.salle, row.salle];
        if (Object.keys(c).length) changes.push({ id: row.id, ...c });
      });
      if (changes.length) dbg('patients diff', changes);
    }
    prevSnapRef.current = snap;
  }, [patients]);

  // 🧭 Décision “bouton d’ajout” vs “carte patient” (occupation des salles de l’étage courant)
  useEffect(() => {
    const grouped = {};
    (patientsActifs || []).forEach((p) => {
      const key = p.salle_id;
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ id: p.id, step: Number(getStep(p)) });
    });
  }, [patientsActifs]);

  const handleToggleUrgence = (id) => {
    const patient = (patients || []).find((p) => p.id === id);
    if (!patient) return;
    const updated = { ...patient, urgence: !patient.urgence };
    dbg('toggleUrgence', { id, from: patient.urgence, to: updated.urgence });
    setPatients((prev) => prev.map((x) => (x.id === id ? updated : x)));
    socket.emit('toggleUrgence', { id, urgence: updated.urgence }, (err) => {
      if (err) {
        socket.emit('toggle_urgence', { id, urgence: updated.urgence }, (err2) => {
          if (err2) {
            setPatients((prev) => prev.map((x) => (x.id === id ? patient : x)));
            alert('❌ Erreur WS urgence');
          }
        });
      }
    });
  };

  /* -------- UI -------- */

  const GroupeRail = ({ value, onChange }) => (
    <div className="sticky bottom-0 z-10 mt-2 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-t border-slate-200">
      <div className="flex w-full flex-wrap items-center justify-center gap-2 px-3 py-3">
        <button
          onClick={() =>
            onChange({
              group_id: 'all-patients',
              group_libelle: 'Tous',
              salles: [],
            })
          }
          className={[
            'relative h-10 min-w-[3rem] px-3 rounded-full text-sm font-semibold border shadow-sm transition',
            value === 'all-patients'
              ? 'bg-gradient-to-r from-[#A51A1F] to-[#DB0E16] text-white border-transparent'
              : 'bg-white border-red-600 text-red-600 hover:bg-red-50',
          ].join(' ')}
          title="Tous les patients"
        >
          {patientsByGroup['all-patients'] !== undefined && (
            <div
              className={
                'absolute -top-2 -right-2 flex items-center justify-center ' +
                'w-5 h-5 rounded-full  text-[10px] font-bold ring-2 ring-red-600 ' +
                (value === 'all-patients' ? 'bg-white text-red-600' : 'bg-red-600 text-white')
              }
            >
              {patientsByGroup['all-patients'].length}
            </div>
          )}
          Tous
        </button>
        {groups.map((mGroup) => (
          <button
            key={mGroup.group_id}
            onClick={() => onChange(mGroup)}
            className={[
              'relative h-10 min-w-[3rem] px-3 rounded-full text-sm font-semibold border shadow-sm transition',
              value === mGroup.group_id
                ? 'bg-gradient-to-r from-[#A51A1F] to-[#DB0E16] text-white border-transparent'
                : 'bg-white border-red-600 text-red-600 hover:bg-red-50',
            ].join(' ')}
            title={mGroup.group_libelle}
          >
            {patientsByGroup[mGroup.group_id] !== undefined && (
              <div
                className={
                  'absolute -top-2 -right-2 flex items-center justify-center ' +
                  'w-5 h-5 rounded-full  text-[10px] font-bold ring-2 ring-red-600 ' +
                  (value === mGroup.group_id ? 'bg-white text-red-600' : 'bg-red-600 text-white')
                }
              >
                {patientsByGroup[mGroup.group_id].length}
              </div>
            )}

            {mGroup.group_libelle}
          </button>
        ))}
      </div>
    </div>
  );

  function PatientLineMobile({ p, onToggleUrgence, onDeleted, isAdmin }) {
    const step = Number(getStep(p)) || 0;
    const tone = toneForStep(step);
    const uid = getUid(p);
    const e = entryOf(p);
    const timeStr = e ? `${showTime(e)} · ${fromNow(e)}` : null;
    const stepLabel = STATUS_MAP.get(step)?.name || '—';
    const salleLabel = getSalleNomFromId(p?.salle_id, salles);

    // 📌 Carte 100% cliquable → fiche patient
    return (
      <Link
        to={`/fiche/${p.id}`}
        className="sm:hidden block rounded-lg border border-slate-200 p-3 bg-white hover:shadow transition"
      >
        {/* Ligne 1: [Salle] | [Urgence + Nom + DDN + UID] */}
        <div className="grid grid-cols-[auto_1fr] items-center gap-3">
          {/* Badge salle (compact) */}
          <div className="shrink-0 flex flex-col items-center gap-1">
            <span
              className="inline-flex h-8 items-center rounded-full px-3 text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(90deg,#A51A1F,#DB0E16)' }}
              title={salleLabel}
            >
              {salleLabel}
            </span>
            {(() => {
              const salleObj = salles.find((s) => s.salle_id === p?.salle_id);
              const typeLibelle = getTypeSalleLibelle(salleObj?.salle_type || salleObj?.type);
              if (!typeLibelle) return null;
              return (
                <span className="text-[9px] text-red-600 font-bold bg-white px-1.5 py-0.5 rounded-full border border-red-200 shadow-sm text-center max-w-[75px] truncate leading-tight">
                  {typeLibelle}
                </span>
              );
            })()}
          </div>

          {/* Infos patient */}
          <div className="min-w-0">
            {/* Urgence inline + Nom (non wrap + ellipsis) */}
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleUrgence?.(p.id);
                }}
                aria-label={p?.urgence ? 'Retirer urgence' : 'Marquer en urgence'}
                className="-m-1 p-1 shrink-0"
                title="Basculer urgence"
              >
                <UrgentIcon
                  className={`h-4 w-4 transition ${
                    p?.urgence ? 'text-[#B3000A] opacity-100' : 'text-[#B3000A] opacity-20'
                  }`}
                />
              </button>
              <div className="block min-w-0">
                <div className="flex items-center gap-2 font-semibold text-[#B3000A] truncate whitespace-nowrap">
                  <span>
                    {p.civilite || ''} {p?.prenom} <span className="uppercase">{p?.nom}</span>
                  </span>

                  {/* Badge URGENCE Mobile */}
                  {p?.urgence && (
                    <span className="inline-flex items-center rounded-full bg-[#B3000A] px-1.5 py-0.5 text-[9px] font-bold text-white shrink-0">
                      URGENCE
                    </span>
                  )}
                </div>
              </div>

              {canDelete && (
                <div className="ml-auto pl-1 shrink-0">
                  <DeletePatientButton
                    patient={p}
                    token={token}
                    onDeleted={(id) => onDeleted?.(id)}
                    size={16}
                  />
                </div>
              )}
            </div>

            {/* DDN + UID sur la même ligne */}
            <div className="mt-0.5 text-[12px] text-gray-600 flex items-center gap-2 min-w-0">
              <span className="shrink-0">
                {p?.dateNaissance
                  ? new Date(p.dateNaissance).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  : '—'}
              </span>
              {uid ? <span className="shrink-0">•</span> : null}
              {uid ? (
                <span className="font-mono text-[11px] text-gray-500 truncate">{uid}</span>
              ) : null}
            </div>
          </div>
        </div>
        {/* Ligne 2: Statut à gauche | Heure à droite (no wrap) */}
        <div className="mt-2 flex items-center justify-end gap-2">
          <span
            className={[
              'inline-flex items-center gap-1 rounded-full px-3 py-2 text-[13px] font-semibold whitespace-nowrap',
              tone.bg,
              tone.text,
            ].join(' ')}
            title={`Statut : ${stepLabel}`}
          >
            <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
            {stepLabel}
          </span>
          <TpBadge value={p.tpFlag ?? p.tp_flag} />

          <div className="text-[12px] text-gray-600 text-center whitespace-normal">
            RDV&nbsp;:&nbsp;
            <span className="font-medium">
              {p.date_rdv ? format(new Date(p.date_rdv), 'dd/MM HH:mm') : 'Sans RDV'}
            </span>{' '}
            {timeStr && (
              <>
                <span>{' - '}Entrée&nbsp;:</span>&nbsp;
                <span className="font-medium">{timeStr}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <PageLayout
      title="Passages"
      navProps={{
        left: { label: 'Voir Arrivée', to: '/arrivee' },
        right: { label: 'Voir Sortie', to: '/sortie' },
        bgColor: 'bg-white',
        buttonClassName: 'border border-red-600 text-red-600 hover:bg-red-50',
      }}
    >
      <div className="space-y-6 px-4">
        {patientsByGroup[group?.group_id]?.map((pssg) => {
          const salle = etablissement?.salles?.find((s) => { return (s.salle_id === pssg.salle_id)});
          return (
            <section
              key={pssg?.id}
              id={`section-${pssg?.id}`}
              className="rounded-xl p-4 bg-rose-50 border border-slate-200 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-start">
                <div className="w-full h-full sm:w-auto hidden sm:flex flex-col items-center gap-1.5">
                  <RoomBadge
                    salle={salle?.salle_nom}
                    className="max-[767px]:w-full max-[767px]:h-10 sm:w-16 sm:h-16 sm:flex-shrink-0"
                  />
                  {getTypeSalleLibelle(salle?.salle_type) && (
                    <span className="text-xs sm:text-sm text-red-600 font-bold bg-white px-2.5 py-0.5 rounded-full border border-red-200 shadow-sm text-center">
                      {getTypeSalleLibelle(salle?.salle_type)}
                    </span>
                  )}
                </div>

                <div className="flex-1 w-full">
                  <div className="sm:hidden space-y-3">
                    <PatientLineMobile
                      key={pssg.id}
                      p={pssg}
                      onToggleUrgence={handleToggleUrgence}
                    />
                  </div>

                  <div className="hidden sm:block">
                    {
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/fiche/${pssg.id}`)}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(`/fiche/${pssg.id}`)}
                        className={`cursor-pointer p-4 rounded-lg border hover:shadow transition ${
                          pssg.urgence ? 'border-red-500 bg-red-50' : 'border-slate-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleUrgence(pssg.id);
                              }}
                              aria-label="Basculer urgence"
                              className="shrink-0 mt-0.5"
                            >
                              <UrgentIcon
                                className={`h-5 w-5 transition-all duration-200 ${
                                  pssg.urgence
                                    ? 'text-[#B3000A] opacity-100'
                                    : 'text-[#B3000A] opacity-20'
                                }`}
                              />
                            </button>
                            
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-base sm:text-lg font-semibold text-[#B3000A] break-words">
                                  {pssg.civilite || ''} {pssg.prenom}{' '}
                                  <span className="uppercase">{pssg.nom}</span>
                                </p>

                                {/* Badge URGENCE */}
                                {pssg.urgence && (
                                  <span className="inline-flex items-center rounded-full bg-[#B3000A] px-2 py-0.5 text-[10px] font-bold text-white shrink-0">
                                    URGENCE
                                  </span>
                                )}
                              </div>

                              <div className="text-[13px] text-gray-600 flex items-center gap-2">
                                <span>
                                  {pssg?.dateNaissance
                                    ? new Date(pssg.dateNaissance).toLocaleDateString('fr-FR', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric',
                                      })
                                    : '—'}
                                </span>
                                {getUid(pssg) && <span>•</span>}
                                {getUid(pssg) && (
                                  <span className="text-xs font-mono text-gray-500 truncate">
                                    {getUid(pssg)}
                                  </span>
                                )}
                              </div>
                            </div>

                            {canDelete && (
                              <DeletePatientButton
                                patient={pssg}
                                token={token}
                                onDeleted={(id) =>
                                  setPatients((ls) => ls.filter((x) => String(x.id) !== String(id)))
                                }
                              />
                            )}
                          </div>

                          {(() => {
                            const sNow = Number(getStep(pssg));
                            const tone = toneForStep(sNow);
                            const label = STATUS_MAP.get(sNow)?.name || '—';
                            return (
                              <>
                                <span
                                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5
                  text-[11px] sm:text-xs font-semibold ${tone.bg} ${tone.text}`}
                                >
                                  <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                                  {label}
                                </span>
                                <TpBadge value={pssg.tpFlag ?? pssg.tp_flag} />
                                {sheetStatus[pssg.id] === 'pending' && (
                                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-slate-600">
                                    <span className="i-tabler-refresh h-3 w-3 animate-spin" />
                                    Calcul Sheet…
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        <div className="mt-2">
                          {Number(getStep(pssg)) !== 7 ? (
                            <StepIndicator
                              steps={[1, 2, 3, 4, 5, 6]}
                              current={Number(getStep(pssg))}
                              size="xs"
                              variant="auto"
                              desktopFull
                              onStepClick={null}
                              className="pointer-events-none select-none opacity-95"
                              colorMap={{ infirmiere: [1, 2, 5, 6], medecin: [3, 4] }}
                            />
                          ) : (
                            <span className="inline-block rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-semibold px-2 py-0.5">
                              {STATUS_MAP.get(7)?.name || 'En sortie'}
                            </span>
                          )}
                        </div>

                        {(() => {
                          const e = entryOf(pssg);
                          if (!e) return null;
                          return (
                            <p className="mt-1 text-xs text-gray-600">
                              RDV :{' '}
                              <span className="font-medium">
                                {pssg.date_rdv
                                  ? format(new Date(pssg.date_rdv), 'dd/MM HH:mm')
                                  : 'Sans RDV'}
                              </span>{' '}
                              {' · '} Entrée :{' '}
                              <span className="font-medium">{showTimeWDate(e)}</span> {' · '}{' '}
                              {pssg.salle_id ? 'En salle' : 'Attente'} :{' '}
                              <span className="font-medium">{fromNow(e)}</span>
                            </p>
                          );
                        })()}
                      </div>
                    }
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {group?.salles && group.salles.length === 0 && group.group_id !== 'all-patients' && (
          <EmptyState
            title="Pas de salles disponibles"
            subtitle="Vérifiez la configuration des groupes de salles"
          />
        )}
      </div>

      <GroupeRail
        value={group?.group_id}
        onChange={(newGroup) => {
          setGroup(newGroup);
          setChosenGroup(newGroup);
        }}
      />
    </PageLayout>
  );
}